import { ThemeConfig } from 'antd';
import { KCCTheme, CustomThemeConfig } from './types';

export const kccThemeConstants = {
  BORDER_RADIUS: {
    NONE: 0,
    INPUT: 24,
    MENUS: 24,
    OVERLAYS: 0,
    PILL: 80,
    POPOVER_SPECIAL: 44,
  },

  SIZE: {
    S50: 4,
    S100: 8,
    S150: 12,
    S200: 16,
    S250: 20,
    S300: 24,
    S400: 32,
    S500: 40,
    S600: 48,
    S700: 64,
    S800: 80,
    S1200: 120,
  },

  CONTROL: {
    HEIGHT_XS: 24,
    HEIGHT_SM: 32,
    HEIGHT: 40,
    HEIGHT_LG: 48,
    INTERACTIVE_SIZE: 16,
    OUTLINE_WIDTH: 2,
    PADDING_HORIZONTAL: 20,
    PADDING_HORIZONTAL_SM: 16,
  },

  LINE_WIDTH: {
    BORDER: 2,
    FOCUS: 2,
  },

  SPACING: {
    XXS: '4px',
    XS: '8px',
    SM: '12px',
    BASE: '16px',
    MD: '20px',
    LG: '24px',
    XL: '32px',
    XXL: '48px',
    XXXL: '64px',
  },

  PADDING: {
    CONTROL_HORIZONTAL: 20,
    CONTROL_HORIZONTAL_SM: 16,
    INLINE_INPUT: 20,
    CONTENT_HORIZONTAL: 40,
    CONTENT_HORIZONTAL_LG: 20,
    CONTENT_HORIZONTAL_SM: 16,
    CONTENT_VERTICAL: 12,
    CONTENT_VERTICAL_LG: 40,
    CONTENT_VERTICAL_SM: 8,
  },

  SCREEN: {
    XS: 414,
    SM: 576,
    MD: 768,
    LG: 1080,
    XL: 1440,
    XXL: 1920,
    CONTENT_MAX_WIDTH: 1144,
    GUTTER: {
      XS: 16,
      SM: 24,
      MD: 24,
      LG: 24,
      XL: 24,
      XXL: 32,
    },
    MARGIN_HORIZONTAL: {
      XS: 23,
      SM: 36,
      MD: 36,
      LG: 48,
      XL: 108,
      XXL: 208,
    },
    VERTICAL_PADDING: {
      XS: 24,
      SM: 48,
      MD: 40,
      LG: 40,
      XL: 40,
      XXL: 48,
    },
  },

  COLORS: {
    // Neutral scale
    WHITE: '#FFFFFF',
    GRAY_50: '#F7F7F7',
    GRAY_100: '#F1F1F1',
    GRAY_200: '#E2E2E2',
    GRAY_250: '#DBDBDB',
    GRAY_300: '#D4D4D4',
    GRAY_400: '#B7B7B7',
    GRAY_500: '#7D7D7D',
    GRAY_600: '#6E6E6E',
    GRAY_700: '#5D5D5D',
    GRAY_800: '#4B4B4B',
    GRAY_850: '#3B3B3B',
    GRAY_900: '#2B2B2B',
    GRAY_950: '#161616',
    BLACK: '#000000',
    TRANSPARENT: 'rgba(0, 0, 0, 0)',

    // Semantic text
    TEXT: '#161616',
    TEXT_SECONDARY: '#5D5D5D',
    TEXT_DISABLED: '#B7B7B7',
    TEXT_PLACEHOLDER: '#6E6E6E',
    TEXT_LIGHT: '#FFFFFF',
    TEXT_HOVER: '#616161',
    TEXT_DESCRIPTION: 'rgba(0, 0, 0, 0.45)',

    // Backgrounds
    BG_CONTAINER: '#FFFFFF',
    BG_LAYOUT: '#F7F7F7',
    BG_ELEVATED: '#FFFFFF',
    BG_MASK: 'rgba(0, 0, 0, 0.25)',

    // Borders
    BORDER: '#F1F1F1',
    BORDER_SECONDARY: '#E2E2E2',
    BORDER_ACTIVE: '#000000',
    BORDER_DISABLED: '#B7B7B7',

    // Fill states
    FILL: 'rgba(0, 0, 0, 0.15)',
    FILL_SECONDARY: 'rgba(0, 0, 0, 0.12)',
    FILL_TERTIARY: 'rgba(0, 0, 0, 0.08)',
    FILL_QUATERNARY: 'rgba(0, 0, 0, 0.04)',

    // Brand
    PRIMARY: '#000000',
    PRIMARY_KARCHER: '#FFED00',
    SUCCESS: '#49A83E',
    WARNING: '#F46C0B',
    ERROR: '#E2021A',
    INFO: '#006EE4',

    // Color palettes
    PETROL: {
      50: '#E9F4F5',
      100: '#BDDDE0',
      200: '#91C6CB',
      300: '#65AFB6',
      400: '#3998A1',
      500: '#238C96',
      600: '#19828C',
    },
    BLUE: {
      50: '#E5F0FC',
      100: '#B5D5F7',
      200: '#82B8F2',
      300: '#4D99EC',
      400: '#1C7EE7',
      500: '#006EE4',
      600: '#0065D2',
      700: '#005CC0',
      800: '#0054AD',
      900: '#004B9B',
      950: '#003977',
      1000: '#00162E',
    },
    GREEN: {
      50: '#EDF6EC',
      100: '#CAE6C7',
      200: '#A6D4A0',
      300: '#80C278',
      400: '#5DB253',
      500: '#49A83E',
      600: '#439B39',
      700: '#3D8D34',
      800: '#37802F',
      900: '#32722A',
      950: '#265720',
      1000: '#0F220C',
      AREA_OUTLINE: '#2E694B',
    },
    ORANGE: {
      50: '#FEF0E7',
      100: '#FCD4B8',
      200: '#FAB787',
      300: '#F79854',
      400: '#F57C26',
      500: '#F46C0B',
      600: '#E0630A',
      700: '#CD5B09',
      800: '#B95208',
      900: '#A64907',
      950: '#7F3806',
      1000: '#311602',
    },
    RED: {
      50: '#FCE6E8',
      100: '#F7B6BD',
      200: '#F1838F',
      300: '#EB4E5F',
      400: '#E51E33',
      500: '#E2021A',
      600: '#D00218',
      700: '#BE0216',
      800: '#AC0214',
      900: '#9A0112',
      925: '#820000',
      950: '#76010E',
      1000: '#2D0005',
    },
  },

  TYPOGRAPHY: {
    FONT_FAMILY: 'Klean, sans-serif',
    FONT_WEIGHT: {
      REGULAR: 400,
      MEDIUM: 500,
      BOLD: 600,
      EXTRA_BOLD: 700,
      BLACK: 800,
      EXTRA_BLACK: 900,
    },
    FONT_SIZE: {
      XXS: 8,
      XS: 10,
      SM: 12,
      BASE: 14,
      LG: 16,
      XL: 18,
      XXL: 24,
      HEADING_5: 18,
      HEADING_4: 20,
      HEADING_3: 24,
      HEADING_2: 36,
      HEADING_1: 44,
    },
    LINE_HEIGHT: {
      BASE: 1.5,
      SM: 1.43,
      LG: 1.5,
      HEADING: 1,
    },
  },
} as const;

export const kccStyledTheme: KCCTheme = {
  colors: kccThemeConstants.COLORS,
  spacing: kccThemeConstants.SPACING,
  borderRadius: kccThemeConstants.BORDER_RADIUS,
  typography: kccThemeConstants.TYPOGRAPHY,
  screen: kccThemeConstants.SCREEN,
  control: kccThemeConstants.CONTROL,
  padding: kccThemeConstants.PADDING,
  lineWidth: kccThemeConstants.LINE_WIDTH,
  size: kccThemeConstants.SIZE,
};

export const kccToken: ThemeConfig['token'] = {
  // --- Typography ---
  fontFamily: 'Klean, sans-serif',
  fontSize: 14,
  fontSizeSM: 12,
  fontSizeLG: 16,
  fontSizeXL: 18,
  fontSizeHeading1: 44,
  fontSizeHeading2: 36,
  fontSizeHeading3: 24,
  fontSizeHeading4: 20,
  fontSizeHeading5: 18,
  lineHeight: 1.5,
  fontWeightStrong: 600,

  // --- Core Colors ---
  colorPrimary: '#000000',
  colorInfo: '#006EE4',
  colorSuccess: '#49A83E',
  colorWarning: '#F46C0B',
  colorError: '#E2021A',

  // --- Neutral Colors ---
  colorText: '#161616',
  colorTextSecondary: '#5D5D5D',
  colorTextDisabled: '#B7B7B7',
  colorBgLayout: '#F7F7F7',
  colorBgContainer: '#FFFFFF',
  colorBgElevated: '#FFFFFF',
  colorBorder: '#F1F1F1',
  colorBorderSecondary: '#E2E2E2',
  colorFillAlter: '#F7F7F7',
  colorBgMask: '#FFFFFF',

  // --- Global Sizing & Borders ---
  borderRadius: 24, // Default base radius
  borderRadiusLG: 44, // Popover special
  sizeUnit: 4,
  sizeStep: 4,
  lineWidth: 2,
  controlHeight: 40,
  controlHeightLG: 48,
  controlHeightSM: 32,
};

export const kccComponentTokens: CustomThemeConfig['components'] = {
  // === CUSTOM COMPONENTS ===
  Chip: {
    itemBg: '#D4D4D4',
    itemPetrolSelectedBg: '#19828C',
    itemSelectedBg: '#2B2B2B',
    itemSelectedColor: '#FFFFFF',
    borderRadius: 80,
  },
  Scheduler: {
    colorAbsenceActive: '#F46C0B',
    colorCriticalActive: '#E2021A',
    colorUnassignedBg: '#F7F7F7',
    colorTodayBg: '#FFFFFF',
    shiftBorderRadius: 0,
  },
  TopNavigation: {
    controlHeight: 40,
    padding: 16,
  },

  // === ANT DESIGN COMPONENT ===
  Button: {
    fontFamily: 'Klean, sans-serif',
    fontSize: 16,
    fontWeight: '700',
    defaultBorderColor: '#000000',
    colorBgTextActive: 'rgba(0, 0, 0, 0.12)',
    colorBorder: '#f1f1f1',
    colorError: '#e2021a',
    colorErrorActive: '#ac0214',
    colorErrorBg: '#fce6e8',
    colorErrorHover: '#d00218',
    colorLink: '#000000',
    colorLinkActive: '#000000',
    colorLinkHover: '#000000',
    colorPrimary: '#000000',
    colorPrimaryActive: '#000000',
    colorPrimaryBorder: '#f1f1f1',
    colorPrimaryHover: '#000000',
    colorText: '#000000',
    colorTextDisabled: '#b7b7b7',
    colorTextLightSolid: '#ffffff',
    borderRadius: 80,
    borderRadiusLG: 80,
    borderRadiusSM: 80,
    controlHeight: 40,
    controlHeightXS: 24,
    controlHeightSM: 32,
    controlHeightLG: 48,
    marginXS: 8,
    paddingContentHorizontal: 40,
    borderColorDisabled: '#e2e2e2',
    dangerColor: '#ffffff',
    defaultBg: 'transparent',
    defaultColor: '#000000',
    defaultGhostBorderColor: '#000000',
    defaultGhostColor: '#ffffff',
    ghostBg: 'rgba(0, 0, 0, 0)',
    groupBorderColor: '#000000',
    linkHoverBg: 'rgba(0, 0, 0, 0)',
    paddingInline: 16,
    paddingInlineLG: 16,
    paddingInlineSM: 16,
    primaryColor: '#ffed00',
    textHoverBg: 'rgba(0, 0, 0, 0.04)',
    colorBgContainer: '#ffffff',
    solidTextColor: '#FFED00',
    colorBgContainerDisabled: '#e2e2e2',
  },
  Tag: {
    borderRadiusSM: 80,
  },
  Radio: {
    borderRadiusSM: 80,
  },
  Avatar: {
    borderRadius: 80,
  },
  Input: {
    borderRadius: 24,
    paddingInline: 20,
    activeShadow: 'none',
    fontFamily: 'Klean, sans-serif',
    colorBorder: '#e2e2e2',
    colorBgContainerDisabled: 'transparent',
  },
  InputNumber: {
    borderRadius: 24,
    paddingInline: 20,
    activeShadow: 'none',
    fontFamily: 'Klean, sans-serif',
    colorBorder: '#e2e2e2',
    colorBgContainerDisabled: 'transparent',
  },
  Select: {
    borderRadius: 24,
    borderRadiusLG: 24,
    borderRadiusSM: 24,
    optionSelectedBg: '#f1f1f1',
    colorTextDescription: kccThemeConstants.COLORS.TEXT_SECONDARY,
    colorBorder: '#e2e2e2',
    colorBgContainerDisabled: 'transparent',
    hoverBorderColor: '#000000',
    activeBorderColor: '#000000',
    colorTextPlaceholder: '#6e6e6e',
    colorText: '#161616',
    fontFamily: 'Klean, sans-serif',
    fontSize: 16,
    fontSizeSM: 14,
    fontSizeLG: 16,
    fontSizeXL: 18,
    lineHeight: 1.5,
    lineWidth: 2,
    padding: 20,
    paddingLG: 20,
    paddingSM: 20,
    paddingXS: 20,
    paddingMD: 20,
    paddingXL: 20,
    boxShadow: '0 0 0 0 #000',
  },
  DatePicker: {
    colorBgContainer: 'transparent',
    colorBgContainerDisabled: 'transparent',
    colorBgElevated: '#ffffff',
    colorBorder: '#000000',
    colorError: '#e2021a',
    colorErrorOutline: '#f1838f',
    colorIcon: '#161616',
    colorIconHover: '#161616',
    colorLink: '#000000',
    colorLinkActive: '#000000',
    colorLinkHover: '#000000',
    colorPrimary: '#000000',
    colorTextLightSolid: '#ffffff',
    controlItemBgActive: kccThemeConstants.COLORS.TRANSPARENT,
    colorPrimaryBorder: '#f1f1f1',
    colorPrimaryHover: '#000000',
    colorSplit: '#f1f1f1',
    colorText: '#161616',
    colorTextDescription: '#5d5d5d',
    colorTextDisabled: '#b7b7b7',
    colorTextHeading: '#161616',
    colorTextPlaceholder: '#b7b7b7',
    colorWarning: '#f46c0b',
    colorWarningOutline: '#fab787',
    controlItemBgHover: kccThemeConstants.COLORS.FILL_QUATERNARY,
    controlOutline: 'rgba(5, 145, 255, 0.1)',
    borderRadius: 24,
    borderRadiusLG: 24,
    borderRadiusSM: 0,
    borderRadiusXS: 0,
    controlHeight: 40,
    controlHeightLG: 40,
    controlHeightSM: 24,
    controlOutlineWidth: 2,
    controlPaddingHorizontal: 12,
    controlPaddingHorizontalSM: 20,
    fontSize: 16,
    fontWeightStrong: 700,
    lineHeight: 1.5,
    lineWidth: 2,
    sizePopupArrow: 0,
    colorFillAlter: 'rgba(0, 0, 0, 0.04)',
    colorTextQuaternary: '#000000',
    activeBorderColor: '#000000',
    cellHoverBg: 'rgba(0, 0, 0, 0.04)',
    hoverBorderColor: '#000000',
    colorErrorHover: '#d00218',
    colorWarningHover: '#e0630a',
    marginXS: 8,
    marginXXS: 4,
    padding: 16,
    paddingSM: 12,
    paddingXS: 8,
    paddingXXS: 4,
    cellHeight: 32,
    paddingBlock: 8,
    paddingInline: 20,
    presetsMaxWidth: 180,
    cellBgDisabled: 'transparent',
  },
  Table: {
    borderRadius: kccThemeConstants.BORDER_RADIUS.NONE,
    borderRadiusOuter: kccThemeConstants.BORDER_RADIUS.NONE,
    headerBorderRadius: kccThemeConstants.BORDER_RADIUS.NONE,
    headerBg: kccThemeConstants.COLORS.GRAY_50,
    headerColor: kccThemeConstants.COLORS.TEXT,
    headerSortActiveBg: kccThemeConstants.COLORS.GRAY_50,
    headerSortHoverBg: kccThemeConstants.COLORS.GRAY_100,
    headerSplitColor: '#F0F0F0',
    rowHoverBg: kccThemeConstants.COLORS.FILL_QUATERNARY,
    rowSelectedBg: kccThemeConstants.COLORS.WHITE,
    rowSelectedHoverBg: '#FAFAFA',
    controlItemBgActive: kccThemeConstants.COLORS.WHITE,
    controlItemBgHover: '#FAFAFA',
    borderColor: kccThemeConstants.COLORS.BORDER,
    colorBgContainer: kccThemeConstants.COLORS.WHITE,
    colorText: kccThemeConstants.COLORS.TEXT,
    colorTextHeading: kccThemeConstants.COLORS.TEXT,
    colorFillAlter: kccThemeConstants.COLORS.GRAY_50,
    colorFillSecondary: kccThemeConstants.COLORS.FILL_SECONDARY,
    colorFillContent: kccThemeConstants.COLORS.FILL_TERTIARY,
    colorIcon: kccThemeConstants.COLORS.GRAY_600,
    colorIconHover: kccThemeConstants.COLORS.TEXT,
    cellPaddingBlock: kccThemeConstants.SIZE.S100 + 2,
    cellPaddingInline: kccThemeConstants.SIZE.S150,
    cellFontSize: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.BASE,
    fontFamily: kccThemeConstants.TYPOGRAPHY.FONT_FAMILY,
    fontWeightStrong: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD,
    padding: kccThemeConstants.SIZE.S200,
    paddingXS: kccThemeConstants.SIZE.S100,
    paddingSM: kccThemeConstants.SIZE.S150,
    lineWidth: 2,
    footerBg: kccThemeConstants.COLORS.GRAY_50,
  },
  Modal: {
    contentBg: '#FFFFFF',
    footerBg: 'rgba(0, 0, 0, 0)',
    headerBg: 'rgba(0, 0, 0, 0)',
    titleColor: '#161616',
    borderRadiusLG: 0,
    borderRadiusSM: 80,
    colorBgMask: 'rgba(0, 0, 0, 0.25)',
    colorError: '#e2021a',
    colorIcon: '#161616',
    colorIconHover: '#616161',
    colorInfo: '#006EE4',
    colorPrimaryBorder: '#f1f1f1',
    colorSuccess: '#49a83e',
    colorText: '#161616',
    colorTextHeading: '#161616',
    colorWarning: '#f46c0b',
    fontSize: 14,
    fontSizeHeading5: 18,
    fontSizeLG: 20,
    fontWeightStrong: 700,
    lineHeight: 1.5,
    lineHeightHeading5: 1,
    lineWidth: 2,
    lineWidthFocus: 2,
    margin: 16,
    marginLG: 24,
    marginSM: 16,
    marginXS: 16,
    padding: 16,
    paddingContentHorizontalLG: 20,
    paddingLG: 20,
    paddingMD: 20,
    paddingXS: 8,
    screenSMMax: 767,
  },
  Card: {
    borderRadiusLG: 0,
    colorBorder: '#F1F1F1',
    colorBorderBg: '#F1F1F1',
    headerBg: '#F1F1F1',
    colorBorderSecondary: '#F1F1F1',
  },
  Collapse: {
    borderRadiusLG: 0,
    headerBg: '#F7F7F7',
  },
  Alert: {
    borderRadiusLG: 0,
    withDescriptionPadding: 20,
  },
  Tabs: {
    borderRadius: 0,
    cardBg: '#F7F7F7',
  },
  Menu: {
    itemBorderRadius: 0,
    subMenuItemBorderRadius: 80,
    itemSelectedBg: '#0054AD',
    itemSelectedColor: '#FFFFFF',
    darkItemSelectedBg: '#000000',
    darkItemBg: '#001529',
  },
  Slider: {
    trackBg: '#000000',
    handleColor: '#000000',
    handleActiveColor: '#000000',
  },
  Switch: {
    handleBg: '#000000',
    handleShadow: '0 0 0 0 #000',
    handleSize: 16,
    handleSizeSM: 12,
    trackHeight: 24,
    trackHeightSM: 16,
    trackMinWidth: 40,
    trackMinWidthSM: 28,
    trackPadding: 2,
    colorPrimary: '#000000',
    colorPrimaryBorder: '#000000',
    colorPrimaryHover: '#000000',
    colorText: '#161616',
    colorTextLightSolid: '#FFFFFF',
    colorTextQuaternary: '#FFFFFF',
    colorTextTertiary: 'none',
    colorWhite: '#FFFFFF',
    controlHeight: 40,
    fontSize: 14,
    fontSizeIcon: 12,
    fontSizeSM: 12,
    lineHeight: 1.5,
    lineWidthFocus: 2,
    marginXS: 8,
    marginXXS: 4,
    lineWidth: 2,
  },
  Upload: {
    actionsColor: 'black',
    borderRadiusLG: 24,
    colorBorder: '#E2E2E2',
    colorFillAlter: '#FFFFFF',
  },
  Form: {
    labelColor: '#161616',
    colorTextDescription: '#5D5D5D',
    labelFontSize: 14,
    labelHeight: 20,
    fontFamily: 'Klean, sans-serif',
    itemMarginBottom: 24,
  },
  Checkbox: {
    colorPrimary: '#000000',
    colorBgContainer: '#fff',
    colorPrimaryHover: '#7d7d7d',
    colorBorder: '#7d7d7d',
    borderRadiusSM: 0,
  },
  TreeSelect: {
    nodeHoverBg: kccThemeConstants.COLORS.FILL_QUATERNARY,
  },
  Divider: {
    colorBorder: '#f1f1f1',
  },
  Typography: {
    colorLink: '#161616',
    colorLinkHover: '#161616',
    colorLinkActive: '#161616',
  },
  Tooltip: {
    borderRadius: 0,
    borderRadiusXS: 0,
    colorBgSpotlight: '#000000',
    colorText: '#161616',
    colorTextLightSolid: '#FFFFFF',
    controlHeight: 40,
    fontSize: 14,
    lineHeight: 1.5,
    sizePopupArrow: 16,
  },
  Segmented: {
    trackBg: '#FFFFFF',
    trackPadding: 2,
    itemColor: '#161616',
    itemHoverColor: '#161616',
    itemHoverBg: 'transparent',
    itemActiveBg: 'transparent',
    itemSelectedBg: '#000000',
    itemSelectedColor: '#FFED00',
    borderRadius: 50,
    borderRadiusLG: 0,
    borderRadiusSM: 80,
    borderRadiusXS: 0,
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 24,
    controlPaddingHorizontal: 20,
    controlPaddingHorizontalSM: 4,
    fontFamily: 'Klean, sans-serif',
    fontSize: 16,
    lineHeight: 1.4,
    colorText: '#161616',
    colorTextDisabled: '#B7B7B7',
    lineWidth: 2,
    lineWidthBold: 2,
    marginSM: 12,
    paddingXXS: 4,
    colorPrimaryBorder: '#F1F1F1',
  },
};
